import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { AccessDenied } from '@/pages/AccessDenied'
import { fetchCells, fetchWarehouse, fetchWarehouses } from '@/warehouse/topologyApi'
import { CreateField, CreateModal } from './CreateModal'
import { RecordPicker } from './RecordPicker'
import { openStocktake, type StocktakeScope } from './warehouseApi'

const SCOPES: StocktakeScope[] = ['full', 'zone', 'cell', 'product']

/**
 * Открытие инвентаризации из списка (#94, `POST /warehouse/stocktakes`).
 *
 * Охват — `full`/`zone`/`cell`/`product`, а не склад: склад задаётся отдельно и есть у любой
 * инвентаризации. Охват «по товару» — один товар по всем ячейкам этого склада (#94,
 * `StocktakeService.openForProduct`): его предлагают после того, как сервер научился его
 * открывать, — раньше форма его не показывала, потому что показать значило обещать отказ.
 *
 * Лист позиций сервер наполняет сам с текущего остатка; здесь его не собирают. Слепой
 * пересчёт по умолчанию включён, как в домене: ожидаемое количество подавляется на сервере,
 * и это его свойство, а не галочка экрана.
 *
 * Склады, зоны и ячейки читаются справочником топологии (`warehouse.topology:read`). У кого
 * его нет (кладовщик на сидах), форма говорит это словами, а не показывает пустой выбор.
 */
export function StocktakeCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [warehouseId, setWarehouseId] = useState('')
  const [scope, setScope] = useState<StocktakeScope>('full')
  const [zoneId, setZoneId] = useState('')
  const [cellId, setCellId] = useState('')
  const [cellQuery, setCellQuery] = useState('')
  const [product, setProduct] = useState<{ id: string; label: string } | null>(null)
  const [blind, setBlind] = useState(true)
  const [notice, setNotice] = useState<string | null>(null)

  const warehouses = useQuery({
    queryKey: ['topology', 'warehouses'],
    queryFn: ({ signal }) => fetchWarehouses(signal),
    retry: false,
  })
  const detail = useQuery({
    queryKey: ['topology', 'warehouse', warehouseId],
    enabled: warehouseId !== '' && (scope === 'zone' || scope === 'cell'),
    queryFn: ({ signal }) => fetchWarehouse(warehouseId, signal),
    retry: false,
  })
  const cells = useQuery({
    queryKey: ['topology', 'cells', zoneId, cellQuery],
    enabled: scope === 'cell' && zoneId !== '',
    queryFn: ({ signal }) => fetchCells(zoneId, { q: cellQuery.trim() || undefined }, signal),
    retry: false,
  })

  const save = useMutation({
    mutationFn: () =>
      openStocktake({
        warehouseId,
        scope,
        zoneId: scope === 'zone' || scope === 'cell' ? zoneId || null : null,
        cellId: scope === 'cell' ? cellId || null : null,
        productId: scope === 'product' ? product?.id ?? null : null,
        blind,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'warehouse.stocktake'] })
      navigate(`/stocktakes/${created.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'warehouse.stocktake', 'create')) return <AccessDenied />

  const topologyDenied = warehouses.error instanceof ApiError && warehouses.error.isForbidden
  const blockedReason =
    warehouseId === ''
      ? t('stocktake.create.needWarehouse')
      : scope === 'zone' && zoneId === ''
        ? t('stocktake.create.needZone')
        : scope === 'cell' && cellId === ''
          ? t('stocktake.create.needCell')
          : scope === 'product' && product === null
            ? t('stocktake.create.needProduct')
            : null

  return (
    <CreateModal
      overline={t('nav.stocktakes')}
      title={t('stocktake.create.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('stocktake.create.submit')}
      onCancel={() => navigate('/stocktakes')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
    >
      <div className="form-grid">
        <CreateField
          id="stocktake-warehouse"
          label={t('column.warehouse_id')}
          required
          span
          help={topologyDenied ? t('stocktake.create.noTopology') : undefined}
        >
          <select
            id="stocktake-warehouse"
            className="input"
            value={warehouseId}
            disabled={!warehouses.data}
            onChange={(event) => {
              setWarehouseId(event.target.value)
              setZoneId('')
              setCellId('')
            }}
          >
            <option value="">{t('stocktake.create.pickWarehouse')}</option>
            {(warehouses.data?.warehouses ?? []).filter((w) => w.active).map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </CreateField>

        <CreateField label={t('column.scope')} required span>
          <div className="row" role="radiogroup" aria-label={t('column.scope')} style={{ gap: 12, flexWrap: 'wrap' }}>
            {SCOPES.map((code) => (
              <label key={code} className="row" style={{ gap: 6 }}>
                <input
                  type="radio"
                  name="stocktake-scope"
                  value={code}
                  checked={scope === code}
                  onChange={() => setScope(code)}
                />
                {t(`value.warehouse.stocktake.scope.${code}`)}
              </label>
            ))}
          </div>
        </CreateField>

        {(scope === 'zone' || scope === 'cell') && (
          <CreateField id="stocktake-zone" label={t('column.zone_id')} required span>
            <select
              id="stocktake-zone"
              className="input"
              value={zoneId}
              disabled={!detail.data}
              onChange={(event) => {
                setZoneId(event.target.value)
                setCellId('')
              }}
            >
              <option value="">{t('stocktake.create.pickZone')}</option>
              {(detail.data?.zones ?? []).filter((zone) => zone.active).map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {`${zone.code} · ${zone.name}`}
                </option>
              ))}
            </select>
          </CreateField>
        )}

        {scope === 'cell' && zoneId !== '' && (
          <>
            <CreateField id="stocktake-cell-search" label={t('stocktake.create.cellSearch')}>
              <input
                id="stocktake-cell-search"
                className="input"
                value={cellQuery}
                onChange={(event) => setCellQuery(event.target.value)}
              />
            </CreateField>
            <CreateField
              id="stocktake-cell"
              label={t('column.cell_id')}
              required
              help={cells.data?.truncated ? t('stocktake.create.cellsTruncated') : undefined}
            >
              <select id="stocktake-cell" className="input" value={cellId} onChange={(event) => setCellId(event.target.value)}>
                <option value="">{t('stocktake.create.pickCell')}</option>
                {(cells.data?.cells ?? []).map((cell) => (
                  <option key={cell.id} value={cell.id}>
                    {cell.code}
                  </option>
                ))}
              </select>
            </CreateField>
          </>
        )}

        {scope === 'product' && (
          <CreateField id="stocktake-product" label={t('column.product_id')} required span help={t('stocktake.create.productHelp')}>
            <RecordPicker
              id="stocktake-product"
              entity="sales.product"
              searchColumn="name"
              extraColumn="sku"
              value={product}
              onChange={setProduct}
            />
          </CreateField>
        )}

        <CreateField id="stocktake-blind" label={t('stocktake.create.blind')} span help={t('stocktake.create.blindHelp')}>
          <input id="stocktake-blind" type="checkbox" checked={blind} onChange={(event) => setBlind(event.target.checked)} />
        </CreateField>
      </div>
    </CreateModal>
  )
}
